import { useEffect, useRef, useState } from "react";
import { Button, Pane } from "../ui/index.js";
import { captureUiError } from "../sentry.js";
function stepDescription(value: unknown): string {
  if (!value || typeof value !== "object" || !("action" in value))
    return "Inspect the recorded step.";
  const step = value as Record<string, unknown>;
  if (step.action === "navigate") return `Open ${String(step.path)}`;
  const locator =
    step.locator && typeof step.locator === "object"
      ? (step.locator as Record<string, unknown>)
      : {};
  const target =
    locator.by === "role"
      ? `${String(locator.role)} “${String(locator.name)}”`
      : `element “${String(locator.value)}”`;
  if (step.action === "click") return `Click ${target}.`;
  if (step.action === "fill")
    return `Enter “${String(step.value)}” in ${target}.`;
  if (step.action === "select")
    return `Select “${String(step.value)}” in ${target}.`;
  if (step.action === "assertText")
    return `Check that ${target} shows exactly “${String(step.text)}”.`;
  return `Check that exactly one matching ${target} is visible.`;
}
export function QaReplayPanel({
  replay,
  validation,
}: {
  replay: {
    id: string;
    name: string;
    definitionHash: string;
    evidenceId?: string;
  };
  validation?: { requestId: string; resultVersion: string; passed: boolean };
}) {
  const [view, setView] = useState<{
      status: string;
      definition: { steps: unknown[] };
    } | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [link, setLink] = useState<string | null>(null);
  const operations = useRef(new Map<string, string>());
  const generation = useRef(0),
    heading = useRef<HTMLHeadingElement>(null),
    confirmation = useRef<HTMLParagraphElement>(null),
    validationLink = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    generation.current++;
    setView(null);
    setBusy(false);
    setError(null);
    setLink(null);
    return () => {
      generation.current++;
    };
  }, [
    replay.id,
    replay.definitionHash,
    validation?.requestId,
    validation?.resultVersion,
  ]);
  useEffect(() => {
    if (view?.status === "accepted") confirmation.current?.focus();
    else if (link) validationLink.current?.focus();
    else if (view) heading.current?.focus();
  }, [view, link]);
  async function act(
    action: "get-qa-replay" | "validate-qa-replay" | "accept-qa-replay",
  ) {
    if (busy) return;
    const startedGeneration = generation.current;
    setBusy(true);
    setError(null);
    const identity = JSON.stringify({
      action,
      replayId: replay.id,
      hash: replay.definitionHash,
      validation,
    });
    if (action !== "get-qa-replay" && !operations.current.has(identity))
      operations.current.set(identity, crypto.randomUUID());
    try {
      const response = await fetch(`/api/worker-requests/actions/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          replayId: replay.id,
          ...(action !== "get-qa-replay"
            ? {
                expectedDefinitionHash: replay.definitionHash,
                operationKey: operations.current.get(identity),
              }
            : {}),
          ...(action === "accept-qa-replay" && validation
            ? {
                validationRequestId: validation.requestId,
                expectedResultVersion: validation.resultVersion,
              }
            : {}),
        }),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(
          result.error ?? "The replay action could not be completed.",
        );
      if (startedGeneration !== generation.current) return;
      if (action === "get-qa-replay") setView(result);
      if (action === "accept-qa-replay")
        setView((current) =>
          current ? { ...current, status: "accepted" } : current,
        );
      if (action === "validate-qa-replay")
        setLink(`/work/${encodeURIComponent(result.workItemId)}`);
      if (action !== "get-qa-replay") operations.current.delete(identity);
    } catch (cause) {
      if (startedGeneration !== generation.current) return;
      setError(
        cause instanceof Error
          ? cause.message
          : "The replay could not be loaded.",
      );
      captureUiError(cause, { area: "Work", operation: "qa_replay_action" });
    } finally {
      if (startedGeneration === generation.current) setBusy(false);
    }
  }
  return (
    <Pane material="staged" className="p-4 space-y-3" aria-busy={busy}>
      <h3 ref={heading} tabIndex={-1}>
        {replay.name}
      </h3>
      <p>
        A replay keeps these steps and assertions fixed. Acceptance applies only
        to this Team, repository, and environment.
      </p>
      {!view ? (
        <Button disabled={busy} onClick={() => void act("get-qa-replay")}>
          Inspect replay
        </Button>
      ) : (
        <>
          <ol className="space-y-2">
            {view.definition.steps.map((step, index) => (
              <li key={index}>
                <p>
                  {index + 1}. {stepDescription(step)}
                </p>
              </li>
            ))}
          </ol>
          <details>
            <summary>Exact replay definition</summary>
            <pre className="whitespace-pre-wrap break-words text-xs">
              {JSON.stringify(view.definition, null, 2)}
            </pre>
          </details>
          {view.status === "accepted" ? (
            <p ref={confirmation} tabIndex={-1} role="status">
              Accepted for this environment.
            </p>
          ) : view.status === "retired" ? (
            <p>This replay is retired.</p>
          ) : validation?.passed ? (
            <Button
              disabled={busy}
              onClick={() => void act("accept-qa-replay")}
            >
              Accept verified replay
            </Button>
          ) : (
            <>
              {validation && (
                <p>
                  The validation did not pass. Inspect the findings before
                  requesting another validation.
                </p>
              )}
              {link ? (
                <a ref={validationLink} href={link}>
                  Open replay validation
                </a>
              ) : (
                <Button
                  disabled={busy}
                  onClick={() => void act("validate-qa-replay")}
                >
                  Validate replay
                </Button>
              )}
              <p>
                Validation starts a disposable browser run without model calls.
                It does not accept the replay.
              </p>
            </>
          )}
        </>
      )}
      {error && <p role="alert">{error} You can retry safely.</p>}
    </Pane>
  );
}
